import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  Grid,
  Card,
  CardContent,
  Divider,
  TablePagination,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { collection, getDocs, deleteDoc, doc, query, writeBatch, getDoc } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import { validateDatabase } from "../../../../Utils/databaseGuard";
import Swal from "sweetalert2";
import dayjs from "dayjs";

const TransactionRequestDeduplicationTool = () => {
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [userFilter, setUserFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [userDataMap, setUserDataMap] = useState(new Map());
  const [stats, setStats] = useState({
    totalTransactionRequests: 0,
    duplicateGroups: 0,
    duplicateEntries: 0,
    affectedUsers: 0,
    individualDuplicates: 0,
    corporateDuplicates: 0,
    totalProfitLossIndividual: 0,
    totalProfitLossCorporate: 0,
    totalProfitLoss: 0,
  });

  const loadUserData = async (userIds) => {
    const userMap = new Map();
    const uniqueUserIds = [...new Set(userIds)];

    for (const userId of uniqueUserIds) {
      try {
        const userDoc = await getDoc(doc(DATABASE, "users", userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          userMap.set(userId, {
            name: userData.full_name || userData.name || "Unknown",
            email: userData.email || "Unknown",
            user_type: userData.user_type !== undefined ? userData.user_type : 0,
          });
        }
      } catch (error) {
        console.error(`Error loading user ${userId}:`, error);
      }
    }

    return userMap;
  };

  const handleAnalyze = async () => {
    setLoading(true);
    setAnalysisComplete(false);
    setDuplicates([]);
    setSelectedDuplicates(new Set());

    try {
      console.log("🔍 Starting transaction request duplicate analysis...");

      // Get ALL transaction requests
      const transRequestsQuery = query(collection(DATABASE, "transection_requests"));
      const transRequestsSnapshot = await getDocs(transRequestsQuery);
      const allTransRequests = transRequestsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log(`📊 Total transaction requests: ${allTransRequests.length}`);

      // Filter for performance transactions (request_type === 2 OR transection_type === 3)
      // SAME LOGIC AS PerformanceLedgerDeduplicationTool
      const performanceTransRequests = allTransRequests.filter(
        (tr) => tr.request_type === 2 || tr.transection_type === 3
      );

      console.log(`📊 Performance transaction requests: ${performanceTransRequests.length}`);

      // Get unique user IDs
      const userIds = new Set(performanceTransRequests.map((tr) => tr.user_id).filter(Boolean));
      console.log(`👥 Loading user data for ${userIds.size} users...`);
      const userMap = await loadUserData(Array.from(userIds));
      setUserDataMap(userMap);

      // SIMPLE AND DIRECT APPROACH: Group by user + month/year, then find duplicates within each group
      // SAME LOGIC AS PerformanceLedgerDeduplicationTool
      const userMonthGroups = new Map();

      performanceTransRequests.forEach((tr) => {
        const userId = tr.user_id;
        if (!userId) return;

        let date = null;
        if (tr.date) {
          date = tr.date?.toDate ? tr.date.toDate() : new Date(tr.date);
        } else if (tr.created_at) {
          date = tr.created_at?.toDate ? tr.created_at.toDate() : new Date(tr.created_at);
        } else {
          console.warn(`⚠️ Transaction ${tr.id} has no date field`);
          return;
        }

        if (isNaN(date.getTime())) {
          console.warn(`⚠️ Transaction ${tr.id} has invalid date`);
          return;
        }

        let month = date.getMonth() + 1;
        let year = date.getFullYear();
        const day = date.getDate();
        const hour = date.getHours();

        // CRITICAL FIX: If entry is migrated and has date at end of month (23:00),
        // it's actually for the NEXT month's performance
        if (tr._migrated_from && tr._migrated_from !== "No") {
          const lastDayOfMonth = new Date(year, month, 0).getDate();
          if (day === lastDayOfMonth && hour >= 23) {
            month = month + 1;
            if (month > 12) {
              month = 1;
              year = year + 1;
            }
          }
        }

        const performance = parseFloat(tr.profit_loss_percent || 0);
        // In transection_requests, the field is 'profit_loss', not 'profit_loss_balance'
        const profitLossBalance = parseFloat(tr.profit_loss || tr.profit_loss_balance || tr.amount || 0);
        const created_at = tr.created_at?.toDate?.() || date;

        const entry = {
          id: tr.id,
          userId,
          date: date.toLocaleString(),
          dateObj: date,
          month,
          year,
          performance,
          profitLossBalance,
          migrated: tr._migrated_from || "No",
          created_at: created_at,
          status: tr.status || tr.request_status || "N/A",
          request_type: tr.request_type || tr.transection_type || "N/A",
          collection: "transection_requests",
        };

        const groupKey = `${userId}-${year}-${String(month).padStart(2, "0")}`;
        if (!userMonthGroups.has(groupKey)) {
          userMonthGroups.set(groupKey, []);
        }
        userMonthGroups.get(groupKey).push(entry);
      });

      // Group entries by same user + month + SAME profit_loss_balance
      // SAME LOGIC AS PerformanceLedgerDeduplicationTool
      const duplicateGroupsMap = new Map();
      let groupIndex = 0;

      userMonthGroups.forEach((entries, groupKey) => {
        if (entries.length > 1) {
          // Group by profit_loss_balance (rounded to 2 decimals to avoid floating point issues)
          const valueGroups = new Map();

          entries.forEach((entry) => {
            // Round to 2 decimals for money comparison
            // If profitLossBalance is 0, use performance % as fallback for grouping
            let valueForGrouping = entry.profitLossBalance;
            if (Math.abs(valueForGrouping) < 0.01 && Math.abs(entry.performance) > 0.0001) {
              // Use performance % as grouping key when balance is zero
              valueForGrouping = entry.performance;
            }
            const profitKey = Math.round(valueForGrouping * 100) / 100;
            const valueKey = profitKey.toFixed(2);

            if (!valueGroups.has(valueKey)) {
              valueGroups.set(valueKey, []);
            }
            valueGroups.get(valueKey).push(entry);
          });

          // Create duplicate groups for entries with SAME profit_loss_balance
          valueGroups.forEach((sameValueEntries, valueKey) => {
            if (sameValueEntries.length > 1) {
              // Verify all entries are from the same month/year (safety check)
              const firstEntry = sameValueEntries[0];
              const allSameMonth = sameValueEntries.every(
                (e) => e.year === firstEntry.year && e.month === firstEntry.month
              );

              if (allSameMonth) {
                duplicateGroupsMap.set(`group-${groupIndex++}`, sameValueEntries);
              }
            }
          });
        }
      });

      console.log(`📊 Found ${duplicateGroupsMap.size} duplicate groups`);

      // Process all duplicate groups
      // SAME LOGIC AS PerformanceLedgerDeduplicationTool
      const duplicateGroups = [];
      duplicateGroupsMap.forEach((entries, key) => {
        // Sort to PREFER keeping MIGRATED entries (from production)
        entries.sort((a, b) => {
          // Priority 1: Prefer MIGRATED entries (from production) - these are CORRECT
          if (a.migrated !== "No" && b.migrated === "No") return -1;
          if (a.migrated === "No" && b.migrated !== "No") return 1;
          // Priority 2: If both migrated or both not migrated, prefer newer date
          const dateA = a.dateObj || new Date(a.date);
          const dateB = b.dateObj || new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        });

        duplicateGroups.push({
          key,
          entries,
          count: entries.length,
          keep: entries[0],
          delete: entries.slice(1),
        });
      });

      // Calculate stats
      const totalDuplicates = duplicateGroups.reduce((sum, group) => sum + group.delete.length, 0);
      const affectedUsers = new Set(duplicateGroups.map((group) => group.entries[0].userId)).size;

      let individualDuplicates = 0;
      let corporateDuplicates = 0;
      let totalProfitLossIndividual = 0;
      let totalProfitLossCorporate = 0;

      duplicateGroups.forEach((group) => {
        const userId = group.entries[0].userId;
        const userData = userMap.get(userId);
        const userType = userData?.user_type !== undefined ? userData.user_type : 0;
        const profitLoss = group.entries[0].profitLossBalance;
        const duplicatesCount = group.delete.length;

        if (userType === 0) {
          individualDuplicates += duplicatesCount;
          totalProfitLossIndividual += profitLoss * duplicatesCount;
        } else {
          corporateDuplicates += duplicatesCount;
          totalProfitLossCorporate += profitLoss * duplicatesCount;
        }
      });

      setStats({
        totalTransactionRequests: performanceTransRequests.length,
        duplicateGroups: duplicateGroups.length,
        duplicateEntries: totalDuplicates,
        affectedUsers,
        individualDuplicates,
        corporateDuplicates,
        totalProfitLossIndividual,
        totalProfitLossCorporate,
        totalProfitLoss: totalProfitLossIndividual + totalProfitLossCorporate,
      });

      setDuplicates(duplicateGroups);
      setAnalysisComplete(true);

      console.log("✅ Analysis complete");
    } catch (error) {
      console.error("❌ Error analyzing duplicates:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to analyze duplicates: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDuplicate = (entryId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    setSelectedDuplicates((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(entryId)) {
        newSelected.delete(entryId);
      } else {
        newSelected.add(entryId);
      }
      return newSelected;
    });
  };

  const handleDelete = async () => {
    if (selectedDuplicates.size === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Selection",
        text: "Please select entries to delete",
      });
      return;
    }

    const confirmed = await Swal.fire({
      icon: "warning",
      title: "Confirm Deletion",
      html: `Are you sure you want to delete <strong>${selectedDuplicates.size}</strong> transaction request entries?<br/><br/>This action cannot be undone!`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#f52a2d",
    });

    if (!confirmed.isConfirmed) return;

    setLoading(true);
    let deleted = 0;
    let errors = 0;
    const userImpact = new Set();

    try {
      const batch = writeBatch(DATABASE);
      let batchCount = 0;

      for (const entryId of selectedDuplicates) {
        try {
          const docRef = doc(DATABASE, "transection_requests", entryId);
          batch.delete(docRef);
          batchCount++;

          // Find which user this belongs to
          const group = duplicates.find((g) => g.delete.some((e) => e.id === entryId));
          if (group) {
            userImpact.add(group.entries[0].userId);
          }

          if (batchCount >= 500) {
            await batch.commit();
            batchCount = 0;
          }
        } catch (error) {
          console.error(`Error deleting ${entryId}:`, error);
          errors++;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      deleted = selectedDuplicates.size - errors;

      Swal.fire({
        icon: "success",
        title: "Success",
        html: `Successfully deleted ${deleted} duplicate transaction requests.<br/><br/>Affected users: ${userImpact.size}<br/>⚠️ Note: This does NOT affect user balances.`,
        width: 600,
      });

      setSelectedDuplicates(new Set());
      setDeleteDialogOpen(false);
      handleAnalyze();
    } catch (error) {
      console.error("❌ Error deleting duplicates:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to delete duplicates: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = () => {
    const allIds = new Set();
    duplicates.forEach((group) => {
      group.delete.forEach((entry) => {
        allIds.add(entry.id);
      });
    });
    setSelectedDuplicates(allIds);
    setSimulateDialogOpen(false);
    setDeleteDialogOpen(true);
  };

  const filteredDuplicates = duplicates.filter((group) => {
    if (!userFilter) return true;
    const userId = group.entries[0].userId;
    const userData = userDataMap.get(userId);
    const userName = userData?.name || "";
    const userEmail = userData?.email || "";
    const searchTerm = userFilter.toLowerCase();
    return (
      userId.toLowerCase().includes(searchTerm) ||
      userName.toLowerCase().includes(searchTerm) ||
      userEmail.toLowerCase().includes(searchTerm)
    );
  });

  const paginatedDuplicates = filteredDuplicates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getUserName = (userId) => {
    const userData = userDataMap.get(userId);
    return userData?.name || userId;
  };

  const getUserEmail = (userId) => {
    const userData = userDataMap.get(userId);
    return userData?.email || "Unknown Email";
  };

  const getUserType = (userId) => {
    const userData = userDataMap.get(userId);
    return userData?.user_type !== undefined ? userData.user_type : 0;
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" sx={{ color: "#fff" }}>
          Transaction Request Deduplication Tool
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleAnalyze}
          disabled={loading}
          sx={{
            background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
            "&:hover": {
              background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
              opacity: 0.9,
            },
          }}
        >
          {loading ? <CircularProgress size={24} /> : "Analyze Duplicates"}
        </Button>
      </Box>

      <Alert severity="warning" sx={{ mb: 3, backgroundColor: "rgba(255, 152, 0, 0.1)", color: "#ff9800" }}>
        <Typography variant="body2">
          <strong>⚠️ IMPORTANT:</strong> This tool finds duplicate performance transaction requests.
          <br />
          <br />
          <strong>✅ SAFE FILTERS:</strong>
          <br />
          • Only analyzes REAL performances (with significant <code>profit_loss_percent</code> OR <code>profit_loss_balance</code>)
          <br />
          • Automatically ignores zero-value transactions (where BOTH percent and balance are zero)
          <br />
          • Automatically ignores test users (Sachin, Test User)
          <br />
          • Only considers duplicates with same <code>profit_loss_balance</code> value
          <br />
          <br />
          <strong>✅ KEEPS:</strong> Migrated entries from production (<code>_migrated_from: fiddux-5a1f1</code>)
          <br />
          <strong>🗑️ DELETES:</strong> Entries created in Rowy (without <code>_migrated_from</code>)
          <br />
          <br />
          <strong>⚠️ ALWAYS REVIEW:</strong> Use "Simulate" button first and verify the list before deleting!
        </Typography>
      </Alert>

      {analysisComplete && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(42, 245, 152, 0.2)" }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: "#2af598" }}>{stats.totalTransactionRequests}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Total Performance Transactions</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(255, 152, 0, 0.2)" }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: "#ff9800" }}>{stats.duplicateGroups}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Duplicate Groups</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(245, 42, 45, 0.2)" }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: "#f52a2d" }}>{stats.duplicateEntries}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Duplicate Entries</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", border: "1px solid rgba(33, 150, 243, 0.2)" }}>
                <CardContent>
                  <Typography variant="h4" sx={{ color: "#2196f3" }}>{stats.affectedUsers}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Affected Users</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Filter by User Name, Email, or ID"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              sx={{ flex: 1, "& .MuiInputBase-input": { color: "#fff" } }}
            />
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={() => setSimulateDialogOpen(true)}
              sx={{ background: "rgba(255, 255, 255, 0.1)" }}
            >
              Simulate
            </Button>
            <Button
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={selectedDuplicates.size === 0}
              sx={{ background: "rgba(255, 255, 255, 0.1)" }}
            >
              Preview ({selectedDuplicates.size})
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={selectedDuplicates.size === 0}
            >
              Delete Selected ({selectedDuplicates.size})
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "rgba(42, 245, 152, 0.2)" }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={filteredDuplicates.length > 0 && filteredDuplicates.every((group) => group.delete.every((e) => selectedDuplicates.has(e.id)))}
                      indeterminate={filteredDuplicates.some((group) => group.delete.some((e) => selectedDuplicates.has(e.id))) && !filteredDuplicates.every((group) => group.delete.every((e) => selectedDuplicates.has(e.id)))}
                      onChange={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const newSelected = new Set(selectedDuplicates);
                        if (e.target.checked) {
                          filteredDuplicates.forEach((group) => {
                            group.delete.forEach((entry) => {
                              newSelected.add(entry.id);
                            });
                          });
                        } else {
                          filteredDuplicates.forEach((group) => {
                            group.delete.forEach((entry) => {
                              newSelected.delete(entry.id);
                            });
                          });
                        }
                        setSelectedDuplicates(newSelected);
                      }}
                      sx={{ color: "#fff" }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#fff" }}>User Name</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Month/Year</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Performance %</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Profit Loss</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Count</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Created At</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Status</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedDuplicates.map((group, index) => {
                  const userType = getUserType(group.entries[0].userId);
                  return (
                    <React.Fragment key={group.key}>
                      <TableRow sx={{ backgroundColor: "rgba(255, 152, 0, 0.1)" }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={group.delete.length > 0 && group.delete.every((e) => selectedDuplicates.has(e.id))}
                            indeterminate={group.delete.some((e) => selectedDuplicates.has(e.id)) && !group.delete.every((e) => selectedDuplicates.has(e.id))}
                            onChange={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const newSelected = new Set(selectedDuplicates);
                              if (e.target.checked) {
                                group.delete.forEach((entry) => {
                                  newSelected.add(entry.id);
                                });
                              } else {
                                group.delete.forEach((entry) => {
                                  newSelected.delete(entry.id);
                                });
                              }
                              setSelectedDuplicates(newSelected);
                            }}
                            sx={{ color: "#fff" }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                              {getUserName(group.entries[0].userId)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#aaa" }}>
                              {getUserEmail(group.entries[0].userId)}
                            </Typography>
                            <Chip
                              label={userType === 0 ? "Individual" : "Corporate"}
                              size="small"
                              sx={{
                                ml: 1,
                                height: 18,
                                fontSize: "0.7rem",
                                backgroundColor: userType === 0 ? "rgba(42, 245, 152, 0.2)" : "rgba(33, 150, 243, 0.2)",
                                color: userType === 0 ? "#2af598" : "#2196f3",
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {String(group.entries[0].month).padStart(2, "0")}/{group.entries[0].year}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>{group.entries[0].performance.toFixed(4)}%</TableCell>
                        <TableCell sx={{ color: "#fff" }}>${group.entries[0].profitLossBalance.toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={`${group.count} entries`}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(255, 152, 0, 0.3)",
                              color: "#fff",
                              fontWeight: "bold",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {dayjs(group.keep.created_at).format("MM/DD/YYYY HH:mm")}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>{group.keep.status}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip
                              label={group.keep.migrated !== "No" ? "✅ Migrated (KEEP)" : "⚠️ Rowy (KEEP)"}
                              size="small"
                              sx={{
                                backgroundColor: group.keep.migrated !== "No" ? "rgba(42, 245, 152, 0.3)" : "rgba(255, 152, 0, 0.3)",
                                color: "#fff",
                                fontWeight: "bold",
                              }}
                            />
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  Swal.fire({
                                    title: "Duplicate Group Details",
                                    html: `
                                      <div style="text-align: left;">
                                        <p><strong>User:</strong> ${getUserName(group.entries[0].userId)}</p>
                                        <p><strong>Email:</strong> ${getUserEmail(group.entries[0].userId)}</p>
                                        <p><strong>Month/Year:</strong> ${String(group.entries[0].month).padStart(2, "0")}/${group.entries[0].year}</p>
                                        <p><strong>Performance:</strong> ${group.entries[0].performance.toFixed(4)}%</p>
                                        <p><strong>Profit Loss:</strong> $${group.entries[0].profitLossBalance.toFixed(2)}</p>
                                        <hr />
                                        <p><strong>✅ KEEPING:</strong> ${group.keep.id}</p>
                                        <p style="color: #2af598;">   • Migrated: ${group.keep.migrated !== "No" ? "YES" : "NO"}</p>
                                        <p style="color: #2af598;">   • Status: ${group.keep.status}</p>
                                        <hr />
                                        <p><strong>🗑️ DELETING:</strong> ${group.delete.length} entries</p>
                                        ${group.delete.map((entry, idx) => `
                                          <p style="color: #f52a2d;">   ${idx + 1}. ${entry.id}</p>
                                          <p style="color: #f52a2d; margin-left: 20px;">   • Status: ${entry.status}</p>
                                          <p style="color: #f52a2d; margin-left: 20px;">   • Migrated: ${entry.migrated !== "No" ? "YES" : "NO"}</p>
                                        `).join("")}
                                      </div>
                                    `,
                                    width: 600,
                                  });
                                }}
                                sx={{ color: "#2196f3" }}
                              >
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      {group.delete.map((entry, deleteIndex) => {
                        const userType = getUserType(entry.userId);
                        const uniqueKey = `${group.key}-delete-${entry.id}-${deleteIndex}`;
                        return (
                          <TableRow key={uniqueKey} sx={{ backgroundColor: "rgba(245, 42, 45, 0.05)" }}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedDuplicates.has(entry.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleSelectDuplicate(entry.id, e);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                sx={{ color: "#fff" }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: "#aaa", pl: 4 }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                  → {getUserName(entry.userId)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#888" }}>
                                  {getUserEmail(entry.userId)}
                                </Typography>
                                <Chip
                                  label={userType === 0 ? "Individual" : "Corporate"}
                                  size="small"
                                  sx={{
                                    ml: 1,
                                    height: 18,
                                    fontSize: "0.7rem",
                                    backgroundColor: userType === 0 ? "rgba(42, 245, 152, 0.2)" : "rgba(33, 150, 243, 0.2)",
                                    color: userType === 0 ? "#2af598" : "#2196f3",
                                  }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: "#aaa" }}>{entry.date}</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>{entry.performance.toFixed(4)}%</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>${entry.profitLossBalance.toFixed(2)}</TableCell>
                            <TableCell>
                              <Chip
                                label={entry.migrated === "No" ? "Created in Rowy" : "Migrated"}
                                size="small"
                                sx={{
                                  backgroundColor: entry.migrated === "No" ? "rgba(245, 42, 45, 0.3)" : "rgba(255, 152, 0, 0.3)",
                                  color: "#fff",
                                  fontWeight: "bold",
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: "#aaa" }}>
                              {dayjs(entry.created_at).format("MM/DD/YYYY HH:mm")}
                            </TableCell>
                            <TableCell sx={{ color: "#aaa" }}>{entry.status}</TableCell>
                            <TableCell sx={{ color: "#f52a2d" }}>Will be deleted</TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredDuplicates.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              sx={{ color: "#fff" }}
            />
          </TableContainer>
        </>
      )}

      {/* Simulate Dialog */}
      <Dialog open={simulateDialogOpen} onClose={() => setSimulateDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ color: "#fff", background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          ⚠️ Simulation Results - Review Before Deleting
        </DialogTitle>
        <DialogContent sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", color: "#fff", maxHeight: "70vh", overflowY: "auto" }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>⚠️ CRITICAL:</strong> Review the list below carefully. This will delete {stats.duplicateEntries} transaction request entries permanently.
              <br />
              <strong>✅ KEEPING:</strong> Migrated entries from production.
              <br />
              <strong>🗑️ DELETING:</strong> Entries created in Rowy (duplicates).
            </Typography>
          </Alert>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ background: "rgba(245, 42, 45, 0.1)", border: "1px solid rgba(245, 42, 45, 0.3)" }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>Total to Delete</Typography>
                  <Typography variant="h4" sx={{ color: "#f52a2d" }}>{stats.duplicateEntries}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ background: "rgba(33, 150, 243, 0.1)", border: "1px solid rgba(33, 150, 243, 0.3)" }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>Affected Users</Typography>
                  <Typography variant="h4" sx={{ color: "#2196f3" }}>{stats.affectedUsers}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: "#f52a2d" }}>
            📋 Detailed List of Entries to be DELETED:
          </Typography>

          <TableContainer component={Paper} sx={{ backgroundColor: "rgba(0, 0, 0, 0.3)", maxHeight: "400px", overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>User</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>Month/Year</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>Performance %</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>Profit Loss</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>Status</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", backgroundColor: "rgba(245, 42, 45, 0.3)" }}>ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {duplicates.flatMap((group) =>
                  group.delete.map((entry) => {
                    const userData = userDataMap.get(entry.userId);
                    return (
                      <TableRow key={entry.id} sx={{ backgroundColor: "rgba(245, 42, 45, 0.05)" }}>
                        <TableCell sx={{ color: "#fff" }}>
                          {userData?.name || entry.userId}
                          <br />
                          <Typography variant="caption" sx={{ color: "#aaa" }}>
                            {userData?.email || "Unknown"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {String(entry.month).padStart(2, "0")}/{entry.year}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>{entry.performance.toFixed(4)}%</TableCell>
                        <TableCell sx={{ color: "#fff" }}>${entry.profitLossBalance.toFixed(2)}</TableCell>
                        <TableCell sx={{ color: "#fff" }}>{entry.status}</TableCell>
                        <TableCell sx={{ color: "#aaa", fontSize: "0.75rem", fontFamily: "monospace" }}>
                          {entry.id.substring(0, 20)}...
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          <Button onClick={() => setSimulateDialogOpen(false)} sx={{ color: "#fff" }}>
            Close
          </Button>
          <Button onClick={handleBulkDelete} variant="contained" color="error">
            Bulk Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: "#fff", background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)", color: "#fff" }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to delete <strong>{selectedDuplicates.size}</strong> transaction request entries.
            <br />
            This action cannot be undone!
          </Alert>
          <Typography variant="body2">
            Selected entries will be permanently deleted from the <code>transection_requests</code> collection.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)" }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: "#fff" }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionRequestDeduplicationTool;

